

import React, { useState, useEffect } from 'react';
import {
    Select,
    Button,
    Card,
    Row,
    Col,
    Space,
    Typography,
    Tag,
    Empty,
    Spin,
    Badge,
    Table,
    Input,
    DatePicker,
    message,
    Descriptions,
    Image,
    List
} from 'antd';
import {
    CalendarOutlined,
    ClearOutlined,
    FilterOutlined,
    SearchOutlined,
    DownloadOutlined,
    ReloadOutlined,
    FileTextOutlined,
    TableOutlined,
    InfoCircleOutlined,
    PictureOutlined,
    FilePdfOutlined,
    EditOutlined
} from '@ant-design/icons';
import InspectionDB from '@/services/indexDB/inspectionStore';
import InspectionBtn from '@/components/InspectionBtn';
import { exportFormatOptions, exportInspectionReport } from '../helper/pdfExport';
import { useSelector } from 'react-redux';
import inspectionService from '@/services/inspection.service';

const { Option } = Select;
const { Text, Title } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const DataList = ({ dataset }) => {
    const [inspectionData, setInspectionData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedInspection, setSelectedInspection] = useState(0);
    const [selectedStep, setSelectedStep] = useState(0);
    const [db] = useState(() => new InspectionDB());
    const [editorVisible, setEditorVisible] = useState(false);
    const [selectedFormat, setSelectedFormat] = useState('pdf');
    const ship = useSelector(data => data?.ship)
    const user = useSelector(data => data?.auth)

     const handleExport = async (inspectionData) => {

        const getShipinfo = await inspectionService.getshipById(ship.selectedShipId);
        const metadata = await db.getFormConfigsByEventType(inspectionData?.eventType);
        const userinfo =
            await exportInspectionReport(inspectionData, getShipinfo, user?.user, metadata?.map(x => x?.formConfigurations)[0], selectedFormat);
    };


    // Filter states
    const [filters, setFilters] = useState({
        eventType: [],
        dateTime: '',
        imoNumber: [],
        searchText: ''
    });

    // Load inspections from IndexedDB
    const loadInspectionsFromDB = async (filters = {}) => {
        setLoading(true);
        try {
            setInspectionData(dataset);
            setFilteredData(dataset);
            if (dataset.length > 0) {
                setSelectedInspection(0);
                setSelectedStep(0);
            }
        } catch (error) {
            console.error('Error loading inspections:', error);
            toast.error('Failed to load inspections from database');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadInspectionsFromDB();
    }, [dataset]);

    // Get unique values for filters
    const getUniqueEventTypes = () => {
        const types = inspectionData?.length ? inspectionData.map(item => item.eventType) : [];
        return [...new Set(types)];
    };

    const getUniqueIMONumbers = () => {
        const imos = inspectionData?.length ? inspectionData.map(item => item.imo_number) : [];
        return [...new Set(imos)];
    };

    const getUniqueDateTimes = () => {
        const dates = inspectionData?.length ? inspectionData.map(item => item.dateTime) : [];
        return [...new Set(dates)];
    };

    // Apply filters
    const applyFilters = () => {
        let filtered = [...inspectionData];

        if (filters.eventType && filters.eventType.length > 0) {
            filtered = filtered.filter(item => filters.eventType.includes(item.eventType));
        }

        if (filters.imoNumber && filters.imoNumber.length > 0) {
            filtered = filtered.filter(item => filters.imoNumber.includes(item.imo_number));
        }

        if (filters.dateTime) {
            filtered = filtered.filter(item => item.dateTime === filters.dateTime);
        }

        if (filters.searchText) {
            filtered = filtered.filter(item =>
                JSON.stringify(item).toLowerCase().includes(filters.searchText.toLowerCase())
            );
        }

        setFilteredData(filtered);
        if (filtered.length > 0) {
            setSelectedInspection(0);
        }
    };

    // Clear filters
    const clearFilters = () => {
        setFilters({
            eventType: [],
            dateTime: '',
            imoNumber: [],
            searchText: ''
        });
        setFilteredData(inspectionData);
        setSelectedInspection(0);
    };


    const currentInspection = filteredData?.length ? filteredData[selectedInspection] : null;

    // Helper function to format field names
    const formatFieldName = (fieldName) => {
        return fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    // Helper function to render field values
    const renderFieldValue = (fieldName, value) => {
        if (!value && value !== 0) return "";

        if (fieldName.toLowerCase().includes('date')) {
            return <Text>{new Date(value).toLocaleDateString()}</Text>;
        }

        if (fieldName.toLowerCase().includes('notes') || fieldName.toLowerCase().includes('description')) {
            return <Text style={{ fontSize: '13px', color: '#666' }}>{value}</Text>;
        }

        return <Text>{value}</Text>;
    };

    // Generate unit table columns
    const generateUnitTableColumns = (step) => {
        if (!step?.units || step.units.length === 0) return [];

        const allFormDataKeys = new Set();
        step.units.forEach(unit => {
            if (unit.form_data) {
                Object.keys(unit.form_data).forEach(key => allFormDataKeys.add(key));
            }
        });

        const columns = [
            {
                title: 'Unit Name',
                dataIndex: 'unit_name',
                key: 'unit_name',
                width: 120,
                render: (text) => (
                    <div>
                        <Text strong className="text-blue-600">{text}</Text>
                    </div>
                )
            }
        ];
        const hasImages = step.units.some(unit => unit.images && unit.images.length > 0);
        if (hasImages) {
            columns.push({
                title: 'Images',
                dataIndex: 'images',
                key: 'images',
                width: 150,
                render: (images) => (
                    <div className="text-center">
                        {images && images.length > 0 ? (
                            <div className="space-y-2">
                                <div className="flex flex-wrap gap-1">
                                    {images.slice(0, 3).map((img, index) => (
                                        <div key={img.image_id || index} className="w-35 h-20 rounded overflow-hidden">
                                            <Image
                                                src={img.image_url}
                                                alt={img.image_name || `Image ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ))}
                                    {images.length > 3 && (
                                        <Text type="secondary" className="text-xs">+{images.length - 3}</Text>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <Text type="secondary" className="text-xs">No images</Text>
                        )}
                    </div>
                )
            });
        }
        // Add columns for each form data field
        Array.from(allFormDataKeys).sort().forEach(fieldName => {
            columns.push({
                title: formatFieldName(fieldName),
                dataIndex: ['form_data', fieldName],
                key: fieldName,
                width: 150,
                render: (value) => renderFieldValue(fieldName, value)
            });
        });

        return columns;
    };

    // Get all step names for current inspection
    const getStepNames = () => {
        if (!currentInspection?.data) return [];
        return currentInspection.data.map(step => ({
            step_name: step.step_name,
            step_index: step.step_index
        }));
    };


    return (
        <div className="min-h-screen bg-gray-50">
            <div className=" mx-auto space-y-4">

                {/* Header */}
                <Card className="shadow-sm " style={{ marginBottom: "5px" }} size='small'>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={5} >Inspection Viewer</Title>
                        </Col>
                        <Col>
                            <Space>

                                <Select
                                    value={selectedFormat}
                                    onChange={setSelectedFormat}
                                    style={{ width: 200 }}
                                    placeholder="Select export format"
                                >
                                    {exportFormatOptions.map(option => (
                                        <Select.Option key={option.value} value={option.value}>
                                            <div>
                                                <div>{option.label}</div>

                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>

                                <InspectionBtn
                                    type="primary"
                                    onClick={() => handleExport(filteredData[0])}
                                    icon={<DownloadOutlined />}
                                >
                                    Export Report
                                </InspectionBtn>
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {/* Filters */}
                <Card size='small' style={{ marginBottom: "5px" }} title={<Space><FilterOutlined />Filters</Space>} className="shadow-sm">
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={8} md={6}>
                            <Text className="text-xs font-medium block mb-1">Event Type</Text>
                            <Select
                                mode="multiple"
                                placeholder="Select event types"
                                value={filters.eventType}
                                onChange={(value) => setFilters(prev => ({ ...prev, eventType: value }))}
                                className="w-full"
                                allowClear
                            >
                                {/* {getUniqueEventTypes().map(type => (
                                    <Option key={type} value={type}>{type ? type?.slice(0, 20) :""}</Option>
                                ))} */}
                            </Select>
                        </Col>
                        <Col xs={24} sm={8} md={6}>
                            <Text className="text-xs font-medium block mb-1">Date Time</Text>
                            <Select
                                placeholder="Select date"
                                value={filters.dateTime}
                                onChange={(value) => setFilters(prev => ({ ...prev, dateTime: value }))}
                                className="w-full"
                                allowClear
                            >
                                {getUniqueDateTimes().map(dateTime => (
                                    <Option key={dateTime} value={dateTime}>
                                        {new Date(dateTime).toLocaleDateString()}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={8} md={6}>
                            <Text className="text-xs font-medium block mb-1">IMO Number</Text>
                            <Select
                                mode="multiple"
                                placeholder="Select IMO numbers"
                                value={filters.imoNumber}
                                onChange={(value) => setFilters(prev => ({ ...prev, imoNumber: value }))}
                                className="w-full"
                                allowClear
                            >
                                {getUniqueIMONumbers().map(imo => (
                                    <Option key={imo} value={imo}>{imo}</Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} md={6}>
                            <Space className='mt-6'>
                                <InspectionBtn type="primary" icon={<SearchOutlined />} onClick={applyFilters}>
                                    Apply Filters
                                </InspectionBtn>
                                <Button icon={<ClearOutlined />} onClick={clearFilters}>
                                    Clear
                                </Button>

                            </Space>
                        </Col>

                    </Row>
                </Card>


                <Spin spinning={loading}>
                    {filteredData && filteredData.length > 0 ? (
                        <Row gutter={16}>

                            {/* Right: All Steps Details */}
                            <Col xs={24} lg={24}>
                                {currentInspection && currentInspection.data ? (
                                    <div className="space-y-6">
                                        {currentInspection.data.map((step, stepIndex) => (
                                            <div key={step.step_index} className="space-y-4">

                                                <Card
                                                    title={
                                                        <Space>

                                                            <div className='flex'>
                                                                <Title level={5} className="mb-0">{step.step_name}</Title>
                                                                <Text type="secondary" className="text-sm ms-3">
                                                                    (IMO: {currentInspection.imo_number} | {new Date(currentInspection.dateTime).toLocaleDateString()})
                                                                </Text>
                                                            </div>
                                                        </Space>
                                                    }
                                                    className="shadow-sm"
                                                >
                                                    {step.units && step.units.length > 0 ? (
                                                        <Table
                                                            columns={generateUnitTableColumns(step)}
                                                            dataSource={step.units.map((unit, index) => ({
                                                                key: `${step.step_index}-${unit.unit_index}`,
                                                                ...unit
                                                            }))}
                                                            pagination={false}
                                                            scroll={{ x: 'max-content' }}
                                                            size="small"
                                                            bordered
                                                        />
                                                    ) : (
                                                        <Empty description="No units found" />
                                                    )}
                                                    <div className='mt-2'>
                                                        {step.common_data ? (
                                                            <div className="space-y-3">
                                                                {step.common_data.form_data && Object.keys(step.common_data.form_data).length > 0 && (
                                                                    <div className="bg-gray-50 rounded-lg p-3">
                                                                        <Text strong className="text-sm mb-2 block">Remarks</Text>
                                                                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                                                            {Object.entries(step.common_data.form_data).map(([key, value]) => (
                                                                                renderFieldValue(key, value) == "" ? "" : <div key={key} className="bg-white rounded p-2 border border-gray-100">
                                                                                    <Text className="text-xs text-gray-600 block mb-1">
                                                                                        {formatFieldName(key)}
                                                                                    </Text>
                                                                                    <Text className="text-sm font-medium">
                                                                                        {renderFieldValue(key, value)}
                                                                                    </Text>
                                                                                </div>

                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {step.common_data.images && step.common_data.images.length > 0 && (
                                                                    <div className="bg-gray-50 rounded-lg p-3">
                                                                        <Text strong className="text-sm mb-2 block">
                                                                            Images ({step.common_data.images.length})
                                                                        </Text>
                                                                        <div className="grid grid-cols-2 sm:grid-cols-12 md:grid-cols-8 gap-2">
                                                                            {step.common_data.images.map((img, index) => (
                                                                                <div
                                                                                    key={img.image_id || index}
                                                                                    className="bg-white rounded-lg p-2 border border-gray-100 hover:shadow-sm transition-shadow"
                                                                                >
                                                                                    <div className="  bg-gray-100 rounded overflow-hidden mb-2">
                                                                                        <Image height={"75%"}
                                                                                            width={"100%"}
                                                                                            src={img.image_url}
                                                                                            alt={img.image_name || `Image ${index + 1}`}
                                                                                            className="w-full object-cover"
                                                                                            preview={{
                                                                                                mask: <div className="text-xs">View</div>
                                                                                            }}
                                                                                        />
                                                                                    </div>

                                                                                    <Text type="secondary" className="text-xs block truncate">
                                                                                        {img.component_name}
                                                                                    </Text>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Empty State */}
                                                                {(!step.common_data.form_data || Object.keys(step.common_data.form_data).length === 0) &&
                                                                    (!step.common_data.images || step.common_data.images.length === 0) && (
                                                                        <div className="text-center py-6 text-gray-500">
                                                                            <Text type="secondary" className="text-sm">No data available</Text>
                                                                        </div>
                                                                    )}
                                                            </div>
                                                        ) : (
                                                            <div className="text-center py-6 text-gray-500">
                                                                <Text type="secondary" className="text-sm">No data available</Text>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Card>

                                                {/* Common Data */}

                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Card className="shadow-sm">
                                        <Empty description="Select an inspection to view all steps" />
                                    </Card>
                                )}
                            </Col>
                        </Row>
                    ) : (
                        <Card className="shadow-sm text-center py-12">
                            <Empty description="No inspection data available">
                                <InspectionBtn type="primary" icon={<ReloadOutlined />} onClick={loadInspectionsFromDB}>
                                    Load Data
                                </InspectionBtn>
                            </Empty>
                        </Card>
                    )}
                </Spin>
            </div>
        </div>
    );
};

export default DataList;